/**
 * TAXONOMY — Industry / Compliance / Certification wire format
 * ============================================================
 * One place that knows what the admin API sends, so neither the management
 * screen nor the attach picker has to.
 *
 * Mirrors Vendors/vendorApi.js deliberately: this API answers "you are not
 * signed in" with HTTP 201 and an empty `data`, so `res.ok` proves nothing
 * and every response goes through unwrap().
 */

const API = process.env.REACT_APP_SERVER_URL;

/** The three vocabularies, in the order every surface renders them. */
export const KINDS = ["industry", "compliance", "certification"];

/** Singular labels for headings and buttons. */
export const KIND_LABEL = {
  industry: "Industry",
  compliance: "Compliance",
  certification: "Certification",
};

/** Plural labels for section headings and tab names. */
export const KIND_PLURAL = {
  industry: "Industries",
  compliance: "Compliances",
  certification: "Certifications",
};

/**
 * Swatches offered by the colour picker. Byte-identical to COLOR_PALETTE in
 * the backends' utils/taxonomy.js — if these drift, a tag created here gets a
 * colour the server would never have assigned.
 */
export const COLOR_PALETTE = [
  "#2563eb",
  "#7c3aed",
  "#db2777",
  "#dc2626",
  "#ea580c",
  "#ca8a04",
  "#16a34a",
  "#0d9488",
  "#0891b2",
  "#4f46e5",
  "#9333ea",
  "#be123c",
];

/* ────────────────────────────────────────────────────────────────────────
   transport
   ──────────────────────────────────────────────────────────────────────── */

async function unwrap(res) {
  let body = null;
  try {
    body = await res.json();
  } catch {
    throw new Error("The server sent a response this screen could not read.");
  }
  if (body?.success === false || body?.status === 201 || body?.status === 202) {
    const err = new Error(body?.msg || "The request was refused.");
    // delete_taxonomy answers 409 with { in_use } so the caller can offer
    // "delete anyway" instead of just reporting failure.
    err.status = body?.status;
    err.data = body?.data;
    throw err;
  }
  if (!res.ok) throw new Error(body?.msg || `Request failed (${res.status}).`);
  return body?.data;
}

const send = (method, path, payload, signal) =>
  fetch(`${API}${path}`, {
    method,
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "include",
    body: payload === undefined ? undefined : JSON.stringify(payload),
    signal,
  }).then(unwrap);

/* ────────────────────────────────────────────────────────────────────────
   reads
   ──────────────────────────────────────────────────────────────────────── */

/**
 * The whole taxonomy in one call — all three vocabularies with usage counts,
 * plus the relation pairs. Small by design (tens of rows), so screens can
 * hold it in state and filter locally rather than round-tripping.
 */
export const loadTaxonomy = (signal) =>
  fetch(`${API}/admin/taxonomy`, {
    headers: { Accept: "application/json" },
    credentials: "include",
    signal,
  }).then(unwrap);

/** What a website listing (all_products.id) currently carries. */
export const getProductTaxonomy = (productId, signal) =>
  fetch(`${API}/admin/taxonomy/product/${productId}`, {
    headers: { Accept: "application/json" },
    credentials: "include",
    signal,
  }).then(unwrap);

/** What an in-app SKU (marketPlaces.id) currently carries. */
export const getSkuTaxonomy = (skuId, signal) =>
  fetch(`${API}/admin/taxonomy/marketplace/${skuId}`, {
    headers: { Accept: "application/json" },
    credentials: "include",
    signal,
  }).then(unwrap);

/* ────────────────────────────────────────────────────────────────────────
   writes
   ──────────────────────────────────────────────────────────────────────── */

/**
 * Bulk create. `names` is an ARRAY — one entry per Enter press in the drawer.
 * Never join them into a comma string: a tag may contain spaces AND commas
 * ("SOC 2, Type II"), so only the array boundary is trustworthy.
 *
 * Names that already exist are skipped server-side, not rejected, so pasting
 * an overlapping list is safe.
 */
export const createTaxonomy = (kind, names, color) =>
  send("POST", "/admin/taxonomy", { kind, names, color: color || undefined });

/** Patch one master row. Renaming re-slugs it, changing its public URL. */
export const updateTaxonomy = (kind, id, patch) =>
  send("PUT", `/admin/taxonomy/${kind}/${id}`, patch);

/**
 * Delete a master row. Throws with `status: 409` and `data.in_use` when it is
 * still attached to products; call again with force to go through anyway.
 */
export const deleteTaxonomy = (kind, id, { force = false } = {}) =>
  send("DELETE", `/admin/taxonomy/${kind}/${id}${force ? "?force=1" : ""}`);

/** Replace which compliances are mandatory in an industry. */
export const setIndustryCompliances = (industryId, complianceIds) =>
  send("PUT", "/admin/taxonomy/relations", {
    kind: "industry",
    id: industryId,
    compliance_ids: complianceIds,
  });

/** Replace which certifications a compliance requires. */
export const setComplianceCertifications = (complianceId, certificationIds) =>
  send("PUT", "/admin/taxonomy/relations", {
    kind: "compliance",
    id: complianceId,
    certification_ids: certificationIds,
  });

/**
 * Replace a website listing's tags. Only the kinds present in `selection` are
 * touched, so a partial save cannot wipe the other two groups.
 */
export const setProductTaxonomy = (productId, selection) =>
  send("PUT", `/admin/taxonomy/product/${productId}`, selection);

/** Replace an in-app SKU's tags. Same partial-update rule. */
export const setSkuTaxonomy = (skuId, selection) =>
  send("PUT", `/admin/taxonomy/marketplace/${skuId}`, selection);

/* ────────────────────────────────────────────────────────────────────────
   shaping
   ──────────────────────────────────────────────────────────────────────── */

/**
 * Deterministic fallback colour, mirroring colorForName() in the backends.
 * Used only when a row somehow has a null colour — the server normally fills
 * it on create, but a row seeded by SQL would not have one.
 */
export function colorForName(name) {
  const slug = String(name ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (!slug) return COLOR_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < slug.length; i += 1) {
    hash = (hash * 31 + slug.charCodeAt(i)) >>> 0;
  }
  return COLOR_PALETTE[hash % COLOR_PALETTE.length];
}

/** The colour a chip should actually render in. */
export const chipColor = (row) => row?.color || colorForName(row?.name);

/**
 * react-select option for a taxonomy row. `color` rides along so the option
 * and the selected chip can both be tinted.
 */
export const toOption = (row) => ({
  value: row.id,
  label: row.name,
  color: chipColor(row),
});

/** Total products carrying a row, across both catalogues. */
export const usageCount = (row) =>
  Number(row?.website_count || 0) + Number(row?.marketplace_count || 0);
